import React, { useState, useRef } from 'react';
import { Box, Container, Typography } from '@mui/material';
import PrimaryContent from '../../Utilities/PrimaryContent';
import PrimaryHeading from '../../Utilities/PrimaryHeading';

export default function WhyNCPro() {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setScrollLeft(containerRef.current.scrollLeft);

    document.body.style.userSelect = 'none';
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setScrollLeft(containerRef.current.scrollLeft);

    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const container = containerRef.current;
    const dx = e.clientX - startX;
    const sliderWidth = container.scrollWidth - container.clientWidth;
    const newScrollLeft = Math.min(
      Math.max(scrollLeft - dx, 0),
      sliderWidth
    );

    container.scrollLeft = newScrollLeft;
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const container = containerRef.current;
    const dx = e.touches[0].clientX - startX;
    const sliderWidth = container.scrollWidth - container.clientWidth;
    const newScrollLeft = Math.min(
      Math.max(scrollLeft - dx, 0),
      sliderWidth
    );

    container.scrollLeft = newScrollLeft;
  };

  const handleMouseUp = () => {
    setIsDragging(false);

    document.body.style.userSelect = 'auto';
  };

  const handleTouchEnd = () => {
    setIsDragging(false);

    document.body.style.userSelect = 'auto';
  };

  const contentArray = [
    {
      topic: "Form the team",
      content: "Partnering with us means you're forming a quality improvement team. From the moment you open NC pro, you're joining forces with our expertise to tackle your quality challenges head-on."
    },
    {
      topic: "Define the Problem",
      content: "Initiate the NCR by defining the problem clearly and precisely. With our AI-assisted problem definition, you ensure accurate and detailed documentation from the start."
    },
    {
      topic: "Containment Plan",
      content: "NC pro helps you to develop a robust containment strategy to address defective or rejected products, ensuring that the issue doesn’t escalate while you're working on a permanent fix."
    },
    {
      topic: "Root Cause Analysis",
      content: "Delve deep into the root causes of non-conformity, identifying escape points and weaknesses in your process. NC pro’s AI capabilities provide insights that go beyond the surface, helping you uncover the true source of the issue."
    },
    {
      topic: "Corrective Actions",
      content: "Formulate targeted corrective actions to eliminate the root cause. Our AI engine suggests innovative solutions based on the data you've provided, offering fresh perspectives and ideas to resolve the issue."
    },
    {
      topic: "Implement the Fix",
      content: "Seamlessly implement corrective actions with the help of our user-friendly interface, ensuring swift and effective resolution of the non-conformity."
    },
    {
      topic: "Prevent Future Issues",
      content: "It goes beyond resolution by helping you establish preventive actions to ensure the issue doesn’t occur again. Our AI-driven insights enable you to foresee potential risks, take proactive measures, allow for horizontal deployment to further strengthen preventive strategies."
    },
    {
      topic: "Review & Celebrate",
      content: "After successfully resolving the non-conformity, NC pro facilitates a review process to ensure all actions are complete. Close the NCR and congratulate your team on a job well done!"
    },
  ];

  return (
    <Container maxWidth={false}
      sx={{
        backgroundImage: `url('/images/whyNcproBg.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: '500px',
        paddingTop: '15px',
        paddingBottom: '15px',
        position: 'relative',
      }}
    >
      <Container maxWidth='xl'>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: '650px',
        }}>
          <PrimaryHeading sx={{
            display: 'flex',
            justifyContent: 'center',
            paddingBottom: '30px',
            pt: 2
          }}><span style={{
            color: 'white',
            background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
            opacity: '90%',
            marginRight:'5px',
          }}>Why</span>NC Pro?
          </PrimaryHeading>
          <PrimaryContent sx={{
            color: 'rgba(95, 119, 191, 1)',
            textAlign: 'center',
            lineHeight: '24px',
            pb: 3
          }}>
            NC pro is fully integrated with the 8D methodology, a globally recognized problem-solving process that systematically addresses non-conformities. Here’s how it works:
          </PrimaryContent>
          <Box
            ref={containerRef}
            sx={{
              display: 'flex',
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              padding: 2,
              gap: 2,
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {contentArray.map((item, index) => (
              <Box
                key={index}
                sx={{
                  minWidth: '335px',
                  minHeight: '300px',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  borderRadius: 5,
                  p: 2,
                  pb: 1.5,
                  px: 3,
                  border: '1px solid #ddd',
                }}
              >
                <Typography variant="h1" sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '25px',
                  fontWeight: 900,
                  mb: 6,
                  lineHeight: '30px',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                }}>
                  <span style={{
                    background: 'linear-gradient(270.26deg, #43B0D7 0.23%, #0D2663 99.77%)',
                    color: 'transparent',
                    backgroundClip: 'text'
                  }}>D{index + 1}</span>
                </Typography>
                <hr style={{ border: '1px solid rgba(230, 247, 253, 1)', borderWidth: '1px', marginBottom: '18px' }} />
                <PrimaryContent variant="subtitle1" sx={{ color: 'rgba(16, 59, 186, 1)', fontWeight: '700', fontStyle: 'italic', mb: 3 }}>
                  {item.topic}
                </PrimaryContent>
                <PrimaryContent>
                  {item.content}
                </PrimaryContent>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Container>
  );
}
